import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { LabelsType, StepCommonProps, Steps } from '../../types';
import { Button } from '@/isomorphic/components/typography/button/button';
import { useLocalization } from '@/hooks/use-localization';
import { useEmploymentFields } from '../../hooks/use-employment-fields';
import { CalculatorStateProps } from '../../branded-consumer-loan';
import { Checkbox } from '@/isomorphic/components/forms/components/checkbox';
import { MockSubmitType } from '../../mock-service';
import { useBrandedColors } from '../../hooks/use-branded-colors';

type StepThreeProps = StepCommonProps & {
    calculator: CalculatorStateProps;
    onSubmit: (mockType?: MockSubmitType) => void;
    validationError?: string | null;
};

export const StepThree = ({
    fields,
    getLabel,
    setStep,
    calculator,
    onSubmit,
    updateFields,
    brandColor,
    scrollToTop,
    focusInFirstInvalid,
}: StepThreeProps) => {
    const { formatCurrency, getLocale } = useLocalization();
    const { loanAmount, tenorYears } = calculator;
    const { active } = useEmploymentFields(fields.applicantEmploymentType);
    const { active: coapplicantActive } = useEmploymentFields(fields.coapplicantEmploymentType, true);
    const { useBrandedColorsBool, getStyle } = useBrandedColors({ brandColor });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const validationState = useMemo(() => {
        const approveConditions = fields.approveConditions === true;
        return {
            approveConditions,
            allFieldsValid: approveConditions,
        };
    }, [fields.approveConditions]);

    useEffect(() => {
        if (showValidationErrors && !validationState.allFieldsValid) {
            focusInFirstInvalid();
        }
    }, [showValidationErrors]);

    const handleSubmit = () => {
        if (isSubmitting) {
            return;
        }

        if (!validationState.allFieldsValid) {
            scrollToTop();
            setShowValidationErrors(true);
            return;
        }

        setIsSubmitting(true);
        onSubmit();
    };

    const formatDate = (year: string | number, month: string | number) => {
        if (!year || !month) {
            return '';
        }

        const date = new Date(Number(year), Number(month) - 1, 1);
        const localeDate = date.toLocaleDateString(getLocale(), {
            year: 'numeric',
            month: 'long',
        });

        return localeDate.charAt(0).toUpperCase() + localeDate.slice(1);
    };

    const formattedYearFrom = useMemo(() => formatDate(fields.applicantEmploymentYearFrom, fields.applicantEmploymentMonthFrom), [fields]);
    const formattedYearUntil = useMemo(() => formatDate(fields.applicantEmploymentYearUntil, fields.applicantEmploymentMonthUntil), [fields]);
    const formattedYearFromCoapplicant = useMemo(
        () => (fields.coapplicantGovernmentId ? formatDate(fields.coapplicantEmploymentYearFrom, fields.coapplicantEmploymentMonthFrom) : ''),
        [fields]
    );
    const formattedYearUntilCoapplicant = useMemo(
        () => (fields.coapplicantGovernmentId ? formatDate(fields.coapplicantEmploymentYearUntil, fields.coapplicantEmploymentMonthUntil) : ''),
        [fields]
    );

    const summaryFields = useMemo(() => {
        const result = [
            {
                title: getLabel('loan'),
                step: 0,
                rows: [
                    [getLabel('loanAmountTitle'), formatCurrency(Number(loanAmount))],
                    [getLabel('tenorTitle'), `${tenorYears} ${getLabel('years')}`],
                ],
            },
            {
                title: getLabel('personalInformation'),
                step: 1,
                rows: [
                    [getLabel('governmentId'), fields.governmentId],
                    [getLabel('phoneMobile'), fields.phoneMobile],
                    [getLabel('contactEmail'), fields.contactEmail],
                    [
                        getLabel('applicantMaritalStatus'),
                        fields.applicantMaritalStatus ? getLabel(fields.applicantMaritalStatus as keyof LabelsType) : '-',
                    ],
                    [getLabel('numberChildrenNotOfAge'), fields.numberChildrenNotOfAge],
                    [
                        getLabel('applicantHabitationForm'),
                        fields.applicantHabitationForm ? getLabel(fields.applicantHabitationForm as keyof LabelsType) : '-',
                    ],
                    [getLabel('employmentType'), fields.applicantEmploymentType ? getLabel(fields.applicantEmploymentType as keyof LabelsType) : '-'],
                    ...(active.employmentTypeQualifying
                        ? [[getLabel('employmentTypeQualifyingText'), fields.employmentTypeQualifying === 'true' ? getLabel('yes') : getLabel('no')]]
                        : []),
                    ...(active.applicantEmployer ? [[getLabel('applicantEmployer'), fields.applicantEmployer]] : []),
                    ...(active.applicantEmploymentYearFrom ? [[getLabel('employmentYearFromTitle'), formattedYearFrom]] : []),
                    ...(active.applicantEmploymentYearUntil ? [[getLabel('employmentYearUntilTitle'), formattedYearUntil]] : []),
                ],
            },
        ];

        if (fields.coapplicantBool) {
            result.push({
                title: getLabel('coapplicant'),
                step: 1,
                rows: [
                    [getLabel('governmentId'), fields.coapplicantGovernmentId],
                    [getLabel('phoneMobile'), fields.coapplicantMobileNumber],
                    [getLabel('contactEmail'), fields.coapplicantEmailAddress],
                    [
                        getLabel('applicantMaritalStatus'),
                        fields.coapplicantMaritalStatus ? getLabel(fields.coapplicantMaritalStatus as keyof LabelsType) : '-',
                    ],
                    [
                        getLabel('applicantHabitationForm'),
                        fields.coapplicantHabitationForm ? getLabel(fields.coapplicantHabitationForm as keyof LabelsType) : '-',
                    ],
                    [
                        getLabel('employmentType'),
                        fields.coapplicantEmploymentType ? getLabel(fields.coapplicantEmploymentType as keyof LabelsType) : '-',
                    ],
                    ...(coapplicantActive.coapplicantEmploymentTypeQualifying
                        ? [
                              [
                                  getLabel('coapplicantEmploymentTypeQualifyingText'),
                                  fields.coapplicantEmploymentTypeQualifying === 'true' ? getLabel('yes') : getLabel('no'),
                              ],
                          ]
                        : []),
                    ...(coapplicantActive.coapplicantEmployer ? [[getLabel('applicantEmployer'), fields.coapplicantEmployer]] : []),
                    ...(coapplicantActive.coapplicantEmploymentYearFrom ? [[getLabel('employmentYearFromTitle'), formattedYearFromCoapplicant]] : []),
                    ...(coapplicantActive.coapplicantEmploymentYearUntil
                        ? [[getLabel('employmentYearUntilTitle'), formattedYearUntilCoapplicant]]
                        : []),
                ],
            });
        }

        result.push({
            title: getLabel('householdEconomyTitle'),
            step: 2,
            rows: [
                [getLabel('monthlyIncomeGross'), formatCurrency(fields.montlyIncomeGross)],
                ...(fields.coapplicantBool
                    ? [[getLabel('coApplicantMonthlyIncomeGross'), formatCurrency(fields.coapplicantMonthlyIncomeGross)]]
                    : []),
                [getLabel('livingExpenses'), formatCurrency(fields.livingExpenses)],
                [getLabel('otherLoanExpenses'), formatCurrency(fields.otherLoanExpenses)],
                [
                    getLabel('transferAccount'),
                    fields.payoutAccount?.clearingNumber || fields.payoutAccount?.accountNumber
                        ? `${fields.payoutAccount?.clearingNumber} - ${fields.payoutAccount?.accountNumber}`
                        : '-',
                ],
                [getLabel('insuranceApproveText'), fields.insurance ? getLabel('yes') : getLabel('no')],
            ],
        });

        return result;
    }, [fields, getLabel, formatCurrency, loanAmount, tenorYears, active, coapplicantActive]);

    return (
        <>
            <div className="r-relative r-mx-auto r-mb-8 r-max-w-2xl r-space-y-4">
                <p className="max-md:r-hidden md:r-pb-4">{getLabel('summaryInfoText')}</p>
                <div
                    className={clsx('r-space-y-6 r-rounded-xl r-border-2 r-bg-white r-p-4', useBrandedColorsBool ? '' : 'r-border-primary-200')}
                    style={useBrandedColorsBool ? { borderColor: brandColor } : {}}
                >
                    {summaryFields.map((section, index) => (
                        <div key={index}>
                            <h4 className={clsx('r-font-semibold', 'r-pb-2 r-text-xl')}>{section.title}</h4>
                            {section.rows.map(([key, value], rowIndex) => (
                                <div key={rowIndex} className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                    <span dangerouslySetInnerHTML={{ __html: key }} />
                                    <span
                                        className={clsx('styled-link r-text-right r-font-bold', useBrandedColorsBool ? 'brand-styles' : '')}
                                        dangerouslySetInnerHTML={{ __html: value ?? '-' }}
                                        style={useBrandedColorsBool ? getStyle() : {}}
                                    />
                                </div>
                            ))}
                            {section.step ? (
                                <Button
                                    brandColor={brandColor}
                                    className="r-mt-4 r-p-2"
                                    size="tiny"
                                    type="secondary"
                                    onClick={() => setStep(section.step! as Steps)}
                                >
                                    {getLabel('change')}
                                </Button>
                            ) : null}
                        </div>
                    ))}
                </div>

                <div className="r-py-8">
                    <h3 className={clsx('r-pb-6 r-text-2xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                        {getLabel('approveConditionsTitle')}
                    </h3>
                    <Checkbox
                        type="checkbox"
                        checked={fields.approveConditions === true}
                        name="approveConditions"
                        onChange={value => updateFields({ approveConditions: value })}
                        showRequiredError={showValidationErrors && !validationState.approveConditions}
                        errorMessage={getLabel('approveConditionsError')}
                        label={
                            <div
                                className={clsx('styled-link', useBrandedColorsBool ? 'brand-styles' : '')}
                                dangerouslySetInnerHTML={{ __html: getLabel('approveConditionsInfo') }}
                                style={useBrandedColorsBool ? getStyle() : {}}
                            />
                        }
                        brandColor={brandColor}
                    />
                </div>

                <div className="r-flex r-gap-4 r-pt-4 max-md:r-flex-col">
                    <Button fullWidth brandColor={brandColor} className="max-md:r-order-1" disabled={isSubmitting} onClick={handleSubmit}>
                        {getLabel('submitForm')}
                    </Button>
                </div>
            </div>
            <div className="r-mx-auto r-mt-8 r-max-w-4xl md:r-px-8">
                <h3
                    className={clsx('r-pb-6 r-text-center r-text-2xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}
                    dangerouslySetInnerHTML={{ __html: getLabel('personalDataPolicyTitle') }}
                />
                <div
                    className={clsx('styled-link r-space-y-4 r-text-sm', useBrandedColorsBool ? 'brand-styles' : '')}
                    dangerouslySetInnerHTML={{ __html: getLabel('personalDataPolicyText') }}
                    style={useBrandedColorsBool ? getStyle() : {}}
                />
            </div>
        </>
    );
};
